import { Position, Trade } from "../types/position";
import {
  PnLCalculation,
  PositionPnL,
  TradePnL,
  PerformanceMetrics,
  MonthlyReturn,
  SymbolPerformance,
} from "../types/pnl";

export class PnLCalculationService {
  /**
   * Calculate P&L for a single position
   */
  static calculatePositionPnL(
    position: Position,
    currentPrice: number,
    includeFees: boolean = true,
  ): PositionPnL {
    // Handle edge cases
    if (position.quantity <= 0) {
      return {
        positionId: position.id,
        tradeId: position.tradeId,
        symbol: position.trade?.symbol || `TRADE_${position.tradeId}`,
        quantity: 0,
        entryPrice: 0,
        currentPrice,
        costBasis: 0,
        marketValue: 0,
        unrealizedPnL: 0,
        realizedPnL: 0,
        totalPnL: 0,
        returnPercentage: 0,
        isOpen: position.status === "OPEN",
      };
    }

    let costBasis: number;
    let entryPrice: number;

    if (position.type === "EXIT" || position.type === "PARTIAL_EXIT") {
      // For exit positions, costBasis is the total cost of the shares being sold
      // We need to calculate the average entry price per share
      costBasis =
        position.costBasis !== undefined
          ? position.costBasis
          : position.quantity * position.price;
      entryPrice =
        costBasis > 0 ? costBasis / position.quantity : position.price;

      // For exit positions, we don't calculate unrealized P&L since they're already closed
      // The realized P&L should come from the position data if available
    } else {
      // For entry positions, use the position's costBasis if available, otherwise calculate from price
      costBasis =
        position.costBasis !== undefined
          ? position.costBasis
          : position.quantity * position.price;
      entryPrice = position.price;
    }

    // Handle zero price case
    if (position.price <= 0) {
      costBasis = 0;
      entryPrice = 0;
    }

    const marketValue = position.quantity * currentPrice;

    let realizedPnL = 0;
    let unrealizedPnL = 0;

    if (position.type === "EXIT" || position.type === "PARTIAL_EXIT") {
      // For exit positions, use the realized P&L from the position data if available
      // Otherwise calculate based on entry vs exit price
      if (position.realizedPnl !== undefined && position.realizedPnl !== null) {
        realizedPnL = position.realizedPnl;
      } else {
        realizedPnL = (position.price - entryPrice) * position.quantity;
      }

      if (includeFees && position.fee) {
        realizedPnL -= position.fee;
      }
    } else {
      // For open positions, calculate unrealized P&L
      unrealizedPnL = (currentPrice - entryPrice) * position.quantity;
      if (includeFees && position.fee) {
        unrealizedPnL -= position.fee;
      }
    }

    const totalPnL = realizedPnL + unrealizedPnL;

    let returnPercentage = 0;
    if (costBasis > 0) {
      returnPercentage = (totalPnL / costBasis) * 100;
      returnPercentage = Math.round(returnPercentage * 100) / 100; // Round to 2 decimal places
    } else if (totalPnL !== 0) {
      // If costBasis is 0 but there's PnL, it's infinite return, handle as 0 or specific value
      returnPercentage = 0; // Or a specific indicator for infinite return
    }

    // Fix negative zero issue
    const fixNegativeZero = (value: number) => (value === 0 ? 0 : value);

    return {
      positionId: position.id,
      tradeId: position.tradeId,
      symbol: position.trade?.symbol || `TRADE_${position.tradeId}`,
      quantity: position.quantity,
      entryPrice,
      currentPrice,
      costBasis,
      marketValue,
      unrealizedPnL: fixNegativeZero(unrealizedPnL),
      realizedPnL: fixNegativeZero(realizedPnL),
      totalPnL: fixNegativeZero(totalPnL),
      returnPercentage,
      isOpen: position.status === "OPEN",
    };
  }

  /**
   * Calculate P&L for an entire trade
   */
  static calculateTradePnL(
    trade: Trade,
    positions: Position[],
    currentPrice: number,
    includeFees: boolean = true,
  ): TradePnL {
    const entryPositions = positions.filter(
      (p) => p.type === "ENTRY" || p.type === "ADD_TO_POSITION",
    );
    const exitPositions = positions.filter(
      (p) => p.type === "EXIT" || p.type === "PARTIAL_EXIT",
    );

    // Calculate entry metrics
    const totalQuantity = entryPositions.reduce(
      (sum, p) => sum + p.quantity,
      0,
    );
    const totalCost = entryPositions.reduce(
      (sum, p) => sum + p.quantity * p.price,
      0,
    );
    const averageEntryPrice =
      totalQuantity > 0
        ? Math.round((totalCost / totalQuantity) * 100) / 100
        : 0;

    // Calculate exit metrics
    const closedQuantity = exitPositions.reduce(
      (sum, p) => sum + p.quantity,
      0,
    );
    const totalProceeds = exitPositions.reduce(
      (sum, p) => sum + p.quantity * p.price,
      0,
    );
    const averageExitPrice =
      closedQuantity > 0
        ? Math.round((totalProceeds / closedQuantity) * 100) / 100
        : 0;

    // Calculate open position metrics
    const openQuantity = totalQuantity - closedQuantity;
    const openCostBasis =
      Math.round(openQuantity * averageEntryPrice * 100) / 100;
    const openMarketValue = Math.round(openQuantity * currentPrice * 100) / 100;

    // Calculate P&L
    const realizedPnL =
      Math.round(
        (averageExitPrice - averageEntryPrice) * closedQuantity * 100,
      ) / 100;
    const unrealizedPnL =
      Math.round((currentPrice - averageEntryPrice) * openQuantity * 100) / 100;
    const totalPnL = Math.round((realizedPnL + unrealizedPnL) * 100) / 100;

    // Calculate fees
    const totalFees = positions.reduce((sum, p) => sum + (p.fee || 0), 0);
    const netPnL = Math.round((totalPnL - totalFees) * 100) / 100;

    // Fix negative zero issue
    const fixNegativeZero = (value: number) => (value === 0 ? 0 : value);

    const finalRealizedPnL = fixNegativeZero(realizedPnL);
    const finalUnrealizedPnL = fixNegativeZero(unrealizedPnL);
    const finalTotalPnL = fixNegativeZero(totalPnL);
    const finalNetPnL = fixNegativeZero(netPnL);

    // Determine trade status
    let status: "OPEN" | "CLOSED" | "PARTIALLY_CLOSED" = "OPEN";
    if (closedQuantity === 0) {
      status = "OPEN";
    } else if (closedQuantity >= totalQuantity) {
      status = "CLOSED";
    } else {
      status = "PARTIALLY_CLOSED";
    }

    const returnPercentage = totalCost > 0 ? (totalPnL / totalCost) * 100 : 0;

    // Calculate position-level P&L
    const positionPnLs = positions.map((p) =>
      this.calculatePositionPnL(p, currentPrice, includeFees),
    );

    return {
      tradeId: trade.id,
      symbol: trade.symbol,
      side: trade.side as "BUY" | "SELL",
      date: trade.date, // Include trade date for accurate monthly grouping
      status,
      entryPrice: averageEntryPrice,
      averageEntryPrice,
      exitPrice: averageExitPrice,
      averageExitPrice,
      quantity: totalQuantity,
      totalQuantity,
      openQuantity,
      closedQuantity,
      totalCost,
      totalProceeds: totalProceeds,
      realizedPnL: finalRealizedPnL,
      unrealizedPnL: finalUnrealizedPnL,
      totalPnL: finalNetPnL, // Total P&L (realized + unrealized) after fees
      netPnL: finalNetPnL, // Net P&L (same as totalPnL for consistency)
      fees: totalFees,
      returnPercentage,
      positionPnLs,
      positions: positionPnLs,
    };
  }

  /**
   * Calculate comprehensive performance metrics across all trades
   */
  static calculatePerformanceMetrics(
    trades: Trade[],
    allPositions: Position[],
    currentPrices: Record<string, number>,
    includeFees: boolean = true,
  ): PerformanceMetrics {
    const tradePnLs: TradePnL[] = [];
    let totalPnL = 0;
    let totalRealizedPnL = 0;
    let totalUnrealizedPnL = 0;
    let totalFees = 0;
    let winningTrades = 0;
    let losingTrades = 0;
    let totalWins = 0;
    let totalLosses = 0;
    let winningTradesFees = 0;
    let losingTradesFees = 0;

    // Calculate P&L for each trade
    for (const trade of trades) {
      const tradePositions = allPositions.filter((p) => p.tradeId === trade.id);
      const currentPrice = currentPrices[trade.symbol] || 0;

      if (tradePositions.length > 0) {
        const tradePnL = this.calculateTradePnL(
          trade,
          tradePositions,
          currentPrice,
          includeFees,
        );
        tradePnLs.push(tradePnL);

        totalPnL += tradePnL.totalPnL;
        totalRealizedPnL += tradePnL.realizedPnL;
        totalUnrealizedPnL += tradePnL.unrealizedPnL;
        totalFees += tradePnL.fees;

        if (tradePnL.totalPnL > 0) {
          // Only count closed trades for win/loss statistics
          if (tradePnL.status === "CLOSED") {
            winningTrades++;
            totalWins += tradePnL.totalPnL;
            winningTradesFees += tradePnL.fees;
          }
        } else if (tradePnL.totalPnL < 0) {
          // Only count closed trades for win/loss statistics
          if (tradePnL.status === "CLOSED") {
            losingTrades++;
            totalLosses += Math.abs(tradePnL.totalPnL);
            losingTradesFees += tradePnL.fees;
          }
        }
      }
    }

    const totalTrades = tradePnLs.length;
    const winRate =
      totalTrades > 0
        ? Math.round((winningTrades / totalTrades) * 10000) / 100
        : 0;

    // Calculate average win/loss consistently - both should exclude fees since fees are tracked separately
    // This provides a cleaner view of trading performance without fee distortion
    const averageWin = winningTrades > 0 ? totalWins / winningTrades : 0;
    const averageLoss = losingTrades > 0 ? totalLosses / losingTrades : 0;
    const profitFactor = averageLoss > 0 ? averageWin / averageLoss : 0;
    const netPnL = totalPnL - totalFees;

    // Find best and worst trades
    const bestTrade =
      tradePnLs.length > 0
        ? tradePnLs.reduce((best, current) =>
            current.totalPnL > best.totalPnL ? current : best,
          )
        : null;

    const worstTrade =
      tradePnLs.length > 0
        ? tradePnLs.reduce((worst, current) =>
            current.totalPnL < worst.totalPnL ? current : worst,
          )
        : null;

    // Calculate monthly returns
    const monthlyReturns = this.calculateMonthlyReturns(tradePnLs);

    // Calculate symbol breakdown
    const symbolBreakdown = this.calculateSymbolBreakdown(tradePnLs);

    // Calculate max drawdown (simplified calculation)
    const maxDrawdown = this.calculateMaxDrawdown(tradePnLs);

    // Calculate Sharpe ratio (simplified, assuming risk-free rate of 0)
    const sharpeRatio = this.calculateSharpeRatio(tradePnLs);

    return {
      totalTrades,
      winningTrades,
      losingTrades,
      winRate,
      averageWin,
      averageLoss,
      profitFactor,
      totalPnL,
      totalRealizedPnL,
      totalUnrealizedPnL,
      maxDrawdown,
      sharpeRatio,
      totalFees,
      netPnL,
      bestTrade,
      worstTrade,
      monthlyReturns,
      symbolBreakdown,
    };
  }

  /**
   * Calculate monthly returns
   */
  private static calculateMonthlyReturns(
    tradePnLs: TradePnL[],
  ): MonthlyReturn[] {
    const monthlyData: Record<string, MonthlyReturn> = {};

    for (const trade of tradePnLs) {
      const month = new Date(trade.date).toISOString().slice(0, 7);

      if (!monthlyData[month]) {
        monthlyData[month] = {
          month,
          realizedPnL: 0,
          unrealizedPnL: 0,
          totalPnL: 0,
          costBasis: 0,
          returnPercentage: 0,
          tradeCount: 0,
        };
      }

      monthlyData[month].realizedPnL += trade.realizedPnL;
      monthlyData[month].unrealizedPnL += trade.unrealizedPnL;
      monthlyData[month].totalPnL += trade.totalPnL;
      monthlyData[month].costBasis += trade.totalCost;
      monthlyData[month].tradeCount++;
    }

    // Calculate return percentages correctly
    Object.values(monthlyData).forEach((monthData) => {
      monthData.returnPercentage =
        monthData.costBasis > 0
          ? (monthData.totalPnL / monthData.costBasis) * 100
          : 0;
      // Round to 2 decimal places for consistency
      monthData.returnPercentage =
        Math.round(monthData.returnPercentage * 100) / 100;
    });

    return Object.values(monthlyData).sort((a, b) =>
      a.month.localeCompare(b.month),
    );
  }

  /**
   * Calculate performance breakdown by symbol
   */
  private static calculateSymbolBreakdown(
    tradePnLs: TradePnL[],
  ): SymbolPerformance[] {
    const symbolData: Record<string, SymbolPerformance> = {};

    for (const trade of tradePnLs) {
      if (!symbolData[trade.symbol]) {
        symbolData[trade.symbol] = {
          symbol: trade.symbol,
          tradeCount: 0,
          winRate: 0,
          totalPnL: 0,
          averagePnL: 0,
          bestTrade: 0,
          worstTrade: 0,
          totalVolume: 0,
        };
      }

      symbolData[trade.symbol].tradeCount++;
      symbolData[trade.symbol].totalPnL += trade.totalPnL;
      symbolData[trade.symbol].totalVolume += trade.totalQuantity;

      if (trade.totalPnL > symbolData[trade.symbol].bestTrade) {
        symbolData[trade.symbol].bestTrade = trade.totalPnL;
      }

      if (trade.totalPnL < symbolData[trade.symbol].worstTrade) {
        symbolData[trade.symbol].worstTrade = trade.totalPnL;
      }
    }

    // Calculate win rates and averages
    Object.values(symbolData).forEach((symbol) => {
      const symbolTrades = tradePnLs.filter((t) => t.symbol === symbol.symbol);
      const winningTrades = symbolTrades.filter((t) => t.totalPnL > 0).length;
      symbol.winRate =
        symbol.tradeCount > 0 ? (winningTrades / symbol.tradeCount) * 100 : 0;
      symbol.averagePnL =
        symbol.tradeCount > 0 ? symbol.totalPnL / symbol.tradeCount : 0;
    });

    return Object.values(symbolData).sort((a, b) => b.totalPnL - a.totalPnL);
  }

  /**
   * Calculate maximum drawdown
   */
  private static calculateMaxDrawdown(tradePnLs: TradePnL[]): number {
    if (tradePnLs.length === 0) return 0;

    let peak = 0;
    let maxDrawdown = 0;
    let runningTotal = 0;

    for (const trade of tradePnLs) {
      runningTotal += trade.totalPnL;
      if (runningTotal > peak) {
        peak = runningTotal;
      }

      const drawdown = peak - runningTotal;
      if (drawdown > maxDrawdown) {
        maxDrawdown = drawdown;
      }
    }

    return maxDrawdown;
  }

  /**
   * Calculate Sharpe ratio (simplified)
   */
  private static calculateSharpeRatio(tradePnLs: TradePnL[]): number {
    if (tradePnLs.length === 0) return 0;

    const returns = tradePnLs.map((t) => t.totalPnL);
    const meanReturn = returns.reduce((sum, r) => sum + r, 0) / returns.length;

    const variance =
      returns.reduce((sum, r) => sum + Math.pow(r - meanReturn, 2), 0) /
      returns.length;
    const standardDeviation = Math.sqrt(variance);

    // Assuming risk-free rate of 0 for simplicity
    return standardDeviation > 0 ? meanReturn / standardDeviation : 0;
  }

  /**
   * Calculate portfolio-level P&L
   */
  static calculatePortfolioPnL(
    trades: Trade[],
    allPositions: Position[],
    currentPrices: Record<string, number>,
    includeFees: boolean = true,
  ): PnLCalculation {
    const performanceMetrics = this.calculatePerformanceMetrics(
      trades,
      allPositions,
      currentPrices,
      includeFees,
    );

    const totalCost = trades.reduce((sum, trade) => {
      const tradePositions = allPositions.filter((p) => p.tradeId === trade.id);
      return (
        sum +
        tradePositions.reduce(
          (posSum, pos) => posSum + pos.quantity * pos.price,
          0,
        )
      );
    }, 0);

    // Calculate current market value
    const currentMarketValue = allPositions.reduce((sum, pos) => {
      const symbol = pos.trade?.symbol || `TRADE_${pos.tradeId}`;
      const currentPrice = currentPrices[symbol] || pos.price;
      return sum + pos.quantity * currentPrice;
    }, 0);

    const returnPercentage =
      totalCost > 0 ? (performanceMetrics.totalPnL / totalCost) * 100 : 0;

    return {
      realizedPnL: performanceMetrics.totalRealizedPnL,
      unrealizedPnL: performanceMetrics.totalUnrealizedPnL,
      totalPnL: performanceMetrics.totalPnL,
      costBasis: totalCost,
      marketValue: currentMarketValue,
      returnPercentage,
    };
  }
}
